// frontend/src/components/Login.jsx
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import './Login.css';

const Login = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [modo, setModo] = useState('login'); // 'login' | 'recuperar'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [recuperarEmail, setRecuperarEmail] = useState('');

  // ============================================
  // HANDLE LOGIN
  // ============================================
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/login/', {
        username: loginData.username,
        password: loginData.password
      });

      if (response.data.success) {
        if (response.data.requires_onboarding) {
          navigate('/onboarding');
          return;
        }

        if (response.data.cuenta_activa) {
          localStorage.setItem('cuenta_activa', JSON.stringify(response.data.cuenta_activa));
        }

        if (onLoginSuccess) onLoginSuccess(response.data);

        const nextUrl = searchParams.get('next');
        navigate(nextUrl || '/');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Usuario o contraseña incorrectos');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // HANDLE RECUPERAR CONTRASEÑA
  // ============================================
  const handleRecuperarPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!recuperarEmail) {
      setError('Por favor ingresa tu correo electrónico');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/password-reset/', { email: recuperarEmail });
      if (response.data.success) {
        setSuccess('Se ha enviado un correo con las instrucciones para recuperar tu contraseña');
        setRecuperarEmail('');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al enviar el correo de recuperación');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="login-container">
      <div className="login-card">

        {/* Brand */}
        <div className="login-brand">
          <span className="login-brand-text">Iniciar sesión</span>
        </div>

        {/* ============================================ */}
        {/* FORMULARIO DE LOGIN                         */}
        {/* ============================================ */}
        {modo === 'login' && (
          <form onSubmit={handleLogin} className="login-form">

            {error && <div className="login-alert login-alert-error">{error}</div>}

            <div className="login-field">
              <label>Usuario</label>
              <input
                type="text"
                value={loginData.username}
                onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                placeholder="usuario"
                required
                disabled={loading}
                autoComplete="username"
              />
            </div>

            <div className="login-field">
              <label>Contraseña</label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                placeholder="••••••••"
                required
                disabled={loading}
                autoComplete="current-password"
              />
            </div>

            <button type="submit" disabled={loading} className="login-btn">
              {loading ? 'Ingresando...' : 'Ingresar'}
            </button>

            <div className="login-footer">
              <button
                type="button"
                className="login-link"
                onClick={() => { setModo('recuperar'); setError(''); setSuccess(''); }}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </form>
        )}

        {/* ============================================ */}
        {/* FORMULARIO DE RECUPERAR CONTRASEÑA          */}
        {/* ============================================ */}
        {modo === 'recuperar' && (
          <form onSubmit={handleRecuperarPassword} className="login-form">

            <p className="login-subtitle">
              Ingresa tu correo y te enviaremos las instrucciones para recuperar tu contraseña.
            </p>

            {error   && <div className="login-alert login-alert-error">{error}</div>}
            {success && <div className="login-alert login-alert-success">{success}</div>}

            <div className="login-field">
              <label>Correo electrónico</label>
              <input
                type="email"
                value={recuperarEmail}
                onChange={(e) => setRecuperarEmail(e.target.value)}
                placeholder="correo@ejemplo.com"
                required
                disabled={loading}
              />
            </div>

            <button type="submit" disabled={loading} className="login-btn">
              {loading ? 'Enviando...' : 'Enviar instrucciones'}
            </button>

            <div className="login-footer">
              <button
                type="button"
                className="login-link"
                onClick={() => { setModo('login'); setError(''); setSuccess(''); }}
              >
                ← Volver al inicio de sesión
              </button>
            </div>
          </form>
        )}

        <hr className="login-divider" />
        <div className="login-footer-links">
          <a href="/instagram/terms-of-service" target="_blank" rel="noopener noreferrer">
            Términos y Condiciones
          </a>
          <span className="login-sep">•</span>
          <a href="/instagram/privacy-policy" target="_blank" rel="noopener noreferrer">
            Políticas de Privacidad
          </a>
        </div>

      </div>
    </div>
  );
};

export default Login;
